import { motion } from 'framer-motion';

const planes = [
  {
    t: 'Inference layer',
    d: 'Model-agnostic gateway, caching, retries, fallback. Cloudflare AI Gateway, Portkey, LiteLLM, Bedrock Gateway.',
  },
  {
    t: 'Agent runtime',
    d: 'Per-entity stateful, hibernatable, scheduled. Cloudflare Agents SDK on Durable Objects; alternatives: Temporal, Inngest, Bedrock AgentCore.',
  },
  {
    t: 'Memory',
    d: 'Vector + episodic + structured. Vectorize, Pinecone, pgvector, Letta, Mem0.',
  },
  {
    t: 'Tools (MCP)',
    d: 'Internal APIs wrapped as MCP servers with OAuth 2.1. 500+ public servers as of Q1 2026.',
  },
  {
    t: 'Evals & CI',
    d: 'Braintrust, Langfuse, OpenAI Evals, DeepEval. Gate promotion on eval thresholds, not test pass.',
  },
  {
    t: 'Observability',
    d: 'OpenTelemetry GenAI conventions v1.40.0 in Datadog, Honeycomb, New Relic, Grafana.',
  },
];

export default function Architecture() {
  return (
    <section id="architecture" className="mx-auto max-w-6xl px-6 py-32">
      <h2 className="font-display text-4xl md:text-6xl tracking-tight2 mb-4">
        The cognition plane.
      </h2>
      <p className="text-ink-dim text-lg max-w-2xl mb-12">
        The new architecture grows a third plane alongside data and control.
        Most of the glue code is no longer yours.
      </p>
      <div className="grid md:grid-cols-3 gap-6">
        {planes.map((b, i) => (
          <motion.div
            key={b.t}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.35, delay: i * 0.04 }}
            className="rounded-2xl border hairline bg-elevated/60 p-6"
          >
            <div className="font-display text-xl tracking-tight2 mb-2">{b.t}</div>
            <p className="text-ink-dim text-sm leading-relaxed">{b.d}</p>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4 }}
        className="mt-10 rounded-2xl border hairline bg-bg/40 p-6 text-sm text-ink-dim leading-relaxed"
      >
        <strong className="text-ink font-medium">Where edge wins.</strong>{' '}
        Every agent step adds latency; 15 hops × 50 ms is the difference between
        snappy and sluggish. Co-locating inference, state, vector search and
        tools collapses the tax — which is why an edge-native stack (Cloudflare
        Workers + Workers AI + Vectorize + Durable Objects + AI Gateway + R2 +
        KV + D1 + Queues + Hyperdrive + Browser Rendering + Agents SDK) reads
        as one stack, not a basket.
        <br />
        <br />
        <strong className="text-ink font-medium">Where edge does not.</strong>{' '}
        Heavy training, large GPU clusters, and named-region sovereignty
        regimes still belong in Bedrock, Vertex AI, or Azure AI Foundry. The
        pragmatic pattern: edge for the agent loop, hyperscaler for the heavy
        iron, R2-or-equivalent zero-egress storage so training data moves
        freely between them.
      </motion.div>
    </section>
  );
}
